import { useEffect, useState } from 'react';
import { ArrowLeft, Globe, PlayCircle, Smartphone, Timer, Wand2, X } from 'lucide-react';
import { trackWebAppClick, webAppHref } from '../webApp';

/**
 * Links into the web app (app.hooshpaz.ir). `link` is an id from WEB_APP_LINKS, which sets the UTM
 * tags and the GA4 event. Plain anchors in the same tab: the visitor keeps one tab, and the web
 * app's own back button returns to its book when there is no app history.
 */
export const WebAppLink = ({ link, slug, className, children, ...rest }) => (
    <a href={webAppHref(link, slug)} className={className} onClick={() => trackWebAppClick(link, slug)} {...rest}>
        {children}
    </a>
);

/** Under the recipe's stats: cook it now in the browser, no install. */
export const RecipeWebAppCard = ({ slug, hasTimers }) => (
    <div className="rd-webapp">
        <div className="rd-webapp-head">
            <span className="rd-webapp-icon"><PlayCircle size={22} aria-hidden="true" /></span>
            <div>
                <b>همین حالا بپز، بدون نصب برنامه</b>
                <small>این دستور را در نسخه وب هوش‌پز باز کن و قدم‌به‌قدم با آن آشپزی کن.</small>
            </div>
        </div>
        <ul className="rd-webapp-points">
            <li><Timer size={15} aria-hidden="true" /> {hasTimers ? 'حالت آشپزی با تایمر هر مرحله' : 'حالت آشپزی مرحله‌به‌مرحله'}</li>
            <li><Wand2 size={15} aria-hidden="true" /> تغییر دستور به سلیقه خودت</li>
            <li><Globe size={15} aria-hidden="true" /> در مرورگر گوشی، بدون دانلود</li>
        </ul>
        <WebAppLink link="recipe_card" slug={slug} className="button rd-webapp-button">
            شروع پخت در نسخه وب هوش‌پز <ArrowLeft size={18} aria-hidden="true" />
        </WebAppLink>
    </div>
);

const MODIFY_EXAMPLES = ['کم‌کالری‌تر', 'گیاهی', 'بدون گلوتن', 'برای مهمانی', 'با موادی که داری'];

/**
 * After the steps: the app's «مال خودت کن» (recipe modification), opened in the web app with
 * `?open=adapt`. The price is stated, as in the apps: a modification costs one credit.
 */
export const RecipeModifyCard = ({ slug }) => (
    <section className="rd-modify" aria-labelledby="rd-modify-title">
        <span className="rd-modify-icon"><Wand2 size={22} aria-hidden="true" /></span>
        <div className="rd-modify-copy">
            <h2 id="rd-modify-title">این دستور را به رژیم و سلیقه خودت تغییر بده</h2>
            <p>هوش‌پز همین دستور را برایت بازنویسی می‌کند؛ مثلاً:</p>
            <ul className="rd-modify-examples">
                {MODIFY_EXAMPLES.map((example) => <li key={example}>{example}</li>)}
            </ul>
            <WebAppLink link="recipe_modify" slug={slug} className="button rd-modify-button">
                تغییر دستور در نسخه وب <ArrowLeft size={18} aria-hidden="true" />
            </WebAppLink>
            <small>هر تغییر ۱ اعتبار؛ نیاز به ورود با شماره موبایل.</small>
        </div>
    </section>
);

const BAR_DISMISSED_KEY = 'hooshpaz.webAppBarDismissed';
const SHOW_AFTER_PX = 700;

const readDismissed = () => {
    try { return window.sessionStorage.getItem(BAR_DISMISSED_KEY) === '1'; } catch { return false; }
};

/**
 * Phone-width bar pinned to the bottom once the visitor scrolls into the recipe; it opens the web
 * app's recipe maker. Closing it hides it for the visit.
 */
export const RecipeWebAppBar = () => {
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(true);

    useEffect(() => {
        setDismissed(readDismissed());
        const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    if (dismissed) return null;

    const close = () => {
        setDismissed(true);
        try { window.sessionStorage.setItem(BAR_DISMISSED_KEY, '1'); } catch { /* storage blocked */ }
    };

    return (
        <div className={`rd-webapp-bar${visible ? ' visible' : ''}`} aria-hidden={!visible}>
            <img src="/hooshpaz-logo.webp" alt="" width="40" height="40" />
            <span className="rd-webapp-bar-copy">
                <b>نسخه وب هوش‌پز رو امتحان کن</b>
                <small>دستور پخت جدیدت رو همین حالا بساز</small>
            </span>
            <WebAppLink link="recipe_sticky_bar" className="button button-small" tabIndex={visible ? 0 : -1}>
                ورود به نسخه وب
            </WebAppLink>
            <button type="button" className="rd-webapp-bar-close" onClick={close} aria-label="بستن" tabIndex={visible ? 0 : -1}>
                <X size={18} />
            </button>
        </div>
    );
};

/**
 * The iPhone option under the Android store buttons. There is no iOS app any more (retired
 * 2026-11-03): iPhone visitors open the web app, which the pill says plainly.
 */
export const IphoneWebAppButton = () => (
    <WebAppLink link="download_iphone" className="store-button store-button-web store-button-iphone">
        <Smartphone size={26} aria-hidden="true" />
        <span><b>آیفون</b><small className="store-pill">نسخه وب، بدون نصب</small></span>
        <ArrowLeft size={20} aria-hidden="true" />
    </WebAppLink>
);
